import { useCallback } from 'react';

import { INITIAL_FALLBACK_ICON_DATA_URL } from '../constants';
import svgCacheManager from '../utils/SvgCacheManager';

import type { DataUrl } from '../types';

/**
 * Sprite 아이콘을 관리하고 조작하는 훅
 * @description
 * - SVG 캐시 매니저를 통해 Data URL 또는 SVGSVGElement 형태로 아이콘 제공
 * - SVG 내 특정 ID의 요소를 추출하고 크기를 조정하는 기능 제공
 * - 메모리 + 로컬스토리지 하이브리드 캐싱으로 성능 최적화
 * @example
 * // const { getSpriteIconDataUrl, getSpriteIconSvg, getTargetSvgElement } = useSpriteIcon();
 * // const dataUrl = await getSpriteIconDataUrl('/icons/sprite.svg');
 * // const svg = await getSpriteIconSvg('/icons/sprite.svg#icon-id');
 * // const element = getTargetSvgElement(svg, { id: 'icon-id', fontSize: 24 });
 */
const useSpriteIcon = () => {
  /**
   * SVG 소스 URL을 Data URL로 변환하는 기능
   * @param src - SVG 소스 URL
   * @returns DataUrl
   * @example
   * // const { getSpriteIconDataUrl } = useSpriteIcon();
   * // const dataUrl = await getSpriteIconDataUrl('/icons/sprite.svg');
   */
  const getSpriteIconDataUrl = useCallback(async (src: string): Promise<DataUrl> => {
    const [origin] = (src as string).split('#');

    try {
      return await svgCacheManager.getDataUrl(origin);
    } catch (error) {
      console.error('Failed to load sprite icon:', error);
      return INITIAL_FALLBACK_ICON_DATA_URL;
    }
  }, []);

  /**
   * SVG 텍스트를 가져오는 기능
   * @param src - SVG 소스 URL
   * @returns SVGSVGElement | null
   * @example
   * // const { getSpriteIconSvg } = useSpriteIcon();
   * // const svg = await getSpriteIconSvg('/icons/sprite.svg');
   */
  const getSpriteIconSvg = useCallback(async (src: string): Promise<SVGSVGElement | null> => {
    try {
      const [origin] = (src as string).split('#');
      const svgText = await svgCacheManager.getSvg(origin);
      const parser = new DOMParser();
      const svg = parser.parseFromString(svgText, 'image/svg+xml').querySelector('svg');
      return svg;
    } catch (error) {
      console.error('Failed to load sprite icon:', error);
      return null;
    }
  }, []);

  /**
   * SVG 내 특정 ID의 요소를 추출하고 크기를 조정하는 기능
   * @param svg - SVGSVGElement
   * @param options - { id: string; fontSize?: number }
   * @returns SVGSVGElement | null
   * @example
   * // const { getTargetSvgElement } = useSpriteIcon();
   * // const element = getTargetSvgElement(svg, { id: 'icon-id', fontSize: 24 });
   */
  const getTargetSvgElement = useCallback(
    (svg: SVGSVGElement | null, options: { id: string; fontSize?: number }) => {
      const { id, fontSize } = options;
      if (!svg) return null;

      const element = svg.getElementById(id) ?? svg.getElementById('skeleton');

      if (!element) return null;

      element.setAttribute('width', fontSize?.toString() ?? '1em');
      element.setAttribute('height', fontSize?.toString() ?? '1em');

      return element.outerHTML;
    },
    [],
  );

  return {
    getSpriteIconDataUrl,
    getSpriteIconSvg,
    getTargetSvgElement,
  };
};

export default useSpriteIcon;
